import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";
import { readTokenClaims, resolveLiveToken } from "../lib/sessionToken";
import { isIos, isStandaloneDisplay } from "./platform";
import { derivePushAvailability, urlBase64ToUint8Array } from "./pushOptIn";

export type PushState =
  | "loading"
  | "unsupported"
  | "ios-needs-install"
  | "blocked"
  | "off"
  | "on";

function pushSupported(): boolean {
  return (
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  );
}

function availability() {
  return derivePushAvailability({
    supported: pushSupported(),
    permission: "Notification" in window ? Notification.permission : "denied",
    ios: isIos(navigator.userAgent, navigator.maxTouchPoints),
    standalone: isStandaloneDisplay(
      window.matchMedia("(display-mode: standalone)").matches,
      (navigator as Navigator & { standalone?: unknown }).standalone,
    ),
  });
}

// Dispositivos ya re-registrados en esta carga de la app (cuenta + endpoint).
const resyncedThisLoad = new Set<string>();

/** Re-registra en el servidor la suscripción que el navegador YA tiene
 * (Eduardo 2026-10-06). Idempotente (upsert por endpoint). Repara: la fila
 * que el servidor borró (410 del servicio de push, endpoint rotado) y el
 * teléfono que volvió a entrar tras una sesión vencida. Best-effort. */
export async function resyncPushSubscription(
  token: string | null | undefined,
  subscription: PushSubscription,
): Promise<void> {
  const live = resolveLiveToken(token);
  if (!live) return;
  const sub = readTokenClaims(live)?.sub ?? "";
  const key = `${sub}|${subscription.endpoint}`;
  if (resyncedThisLoad.has(key)) return;
  resyncedThisLoad.add(key);
  try {
    const json = subscription.toJSON();
    await api.push.subscribe(live, {
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh ?? "",
      auth: json.keys?.auth ?? "",
      user_agent: navigator.userAgent.slice(0, 255),
    });
  } catch {
    resyncedThisLoad.delete(key); // sin red: se reintenta en la siguiente carga
  }
}

export function usePushSubscription() {
  const { token, isImpersonating } = useAuth();
  const [state, setState] = useState<PushState>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (isImpersonating) {
      // Impersonando: suscribir aquí ligaría el dispositivo del admin al jugador.
      setState("unsupported");
      return;
    }
    const avail = availability();
    if (avail !== "ready") {
      setState(avail);
      return;
    }
    try {
      const keyResponse = await api.push.publicKey();
      if (!keyResponse.data.configured) {
        setState("unsupported"); // servidor sin VAPID: ocultar la UI
        return;
      }
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      setState(subscription ? "on" : "off");
      // Con sesión, el dispositivo que ya tiene avisos se vuelve a registrar.
      if (subscription && token) void resyncPushSubscription(token, subscription);
    } catch {
      // Sin red/servidor: mejor ocultar que ofrecer un botón que fallará.
      setState("unsupported");
    }
  }, [isImpersonating, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (isImpersonating) return;
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "blocked" : "off");
        return;
      }
      const keyResponse = await api.push.publicKey();
      if (!keyResponse.data.configured || !keyResponse.data.public_key) {
        setError("No se pudo activar. Intenta de nuevo.");
        return;
      }
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setError("No se pudo activar. Intenta de nuevo.");
        return;
      }
      const applicationServerKey = urlBase64ToUint8Array(keyResponse.data.public_key);
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey as BufferSource,
        }));
      const json = subscription.toJSON();
      await api.push.subscribe(token, {
        endpoint: subscription.endpoint,
        p256dh: json.keys?.p256dh ?? "",
        auth: json.keys?.auth ?? "",
        user_agent: navigator.userAgent.slice(0, 255),
      });
      setState("on");
    } catch {
      setError("No se pudo activar. Intenta de nuevo.");
      setState("off");
    } finally {
      setBusy(false);
    }
  }, [token, isImpersonating]);

  const disable = useCallback(async () => {
    if (isImpersonating) return;
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe().catch(() => {});
        await api.push.unsubscribe(token, endpoint).catch(() => {});
      }
      setState("off");
    } catch {
      setError("No se pudo desactivar. Intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  }, [token, isImpersonating]);

  return { state, busy, error, enable, disable };
}
